import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Check, Phone, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { demoProfessionalAgenda, generateTimeSlots, formatDuration, formatPrice, weekDays } from '../data/agendaMock';
import { siteData } from '../data/mock';

const AgendaPublica = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [professional, setProfessional] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1); // 1: servico, 2: data/hora, 3: dados, 4: confirmado
  
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [clientData, setClientData] = useState({ name: '', phone: '' });
  const [appointments, setAppointments] = useState([]);

  useEffect(() => {
    const loadProfessional = async () => {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Tentar buscar do MongoDB primeiro
      try {
        const response = await fetch(`${API_URL}/api/agenda/professionals/${slug}`);
        if (response.ok) {
          const data = await response.json();
          // Converter formato do backend para o frontend
          const prof = {
            ...data,
            cpfCnpj: data.cpf_cnpj,
            workingHours: data.working_hours || {}
          };
          setProfessional(prof);
          setLoading(false);
          return;
        }
      } catch (error) {
        console.log('Fallback to localStorage/demo');
      }
      
      // Fallback: localStorage ou demo
      const stored = localStorage.getItem(`agenda_${slug}`);
      if (stored) {
        setProfessional(JSON.parse(stored));
      } else if (slug === 'barbearia-do-ze' || slug === 'demo') {
        setProfessional(demoProfessionalAgenda);
      }
      
      setLoading(false);
    };

    loadProfessional();
    
    // Carregar agendamentos existentes
    const storedAppointments = localStorage.getItem(`appointments_${slug}`);
    if (storedAppointments) {
      setAppointments(JSON.parse(storedAppointments));
    }
  }, [slug]);

  // Gerar dias do mês
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDay = firstDay.getDay();
    const totalDays = lastDay.getDate();
    
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Dias vazios antes do primeiro dia
    for (let i = 0; i < startingDay; i++) {
      days.push({ day: null, date: null });
    }
    
    // Dias do mês
    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(year, month, day);
      const dayOfWeek = weekDays[date.getDay() === 0 ? 6 : date.getDay() - 1]?.key || 'sunday';
      const workingHour = professional?.workingHours?.[dayOfWeek];
      const isPast = date < today;
      const isAvailable = workingHour?.enabled && !isPast;
      
      days.push({
        day,
        date,
        dayOfWeek,
        isPast,
        isAvailable,
        isToday: date.toDateString() === today.toDateString()
      });
    }
    
    return days;
  };

  // Gerar horários para o dia selecionado
  const getAvailableSlots = () => {
    if (!selectedDate || !selectedService || !professional) return [];
    
    const dayOfWeek = weekDays[selectedDate.getDay() === 0 ? 6 : selectedDate.getDay() - 1]?.key || 'sunday';
    const workingHours = professional.workingHours?.[dayOfWeek];
    
    if (!workingHours?.enabled) return [];
    
    // Filtrar agendamentos do dia
    const dayAppointments = appointments.filter(apt => 
      new Date(apt.date).toDateString() === selectedDate.toDateString()
    );
    
    return generateTimeSlots(workingHours, selectedService.duration, dayAppointments);
  };

  // Confirmar agendamento
  const handleConfirmAppointment = async () => {
    const dateStr = selectedDate.toISOString().split('T')[0]; // "2025-01-07"
    
    const appointmentData = {
      professional_slug: slug,
      service_id: selectedService.id,
      service_name: selectedService.name,
      service_duration: selectedService.duration,
      service_price: selectedService.price,
      date: dateStr,
      time: selectedTime,
      client: {
        name: clientData.name,
        phone: clientData.phone,
        email: clientData.email || null
      }
    };
    
    try {
      // Salvar no backend
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/agenda/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(appointmentData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        alert(error.detail || 'Erro ao criar agendamento');
        return;
      }
      
      // Também salvar localmente para backup
      const newAppointment = {
        id: Date.now(),
        service: selectedService,
        date: selectedDate.toISOString(),
        time: selectedTime,
        client: clientData,
        status: 'confirmed',
        createdAt: new Date().toISOString()
      };
      
      const updatedAppointments = [...appointments, newAppointment];
      setAppointments(updatedAppointments);
      localStorage.setItem(`appointments_${slug}`, JSON.stringify(updatedAppointments));
      
      setStep(4);
    } catch (error) {
      console.error('Erro ao salvar agendamento:', error);
      // Fallback: salvar só localmente
      const newAppointment = {
        id: Date.now(),
        service: selectedService,
        date: selectedDate.toISOString(),
        time: selectedTime,
        client: clientData,
        status: 'confirmed',
        createdAt: new Date().toISOString()
      };
      
      const updatedAppointments = [...appointments, newAppointment];
      setAppointments(updatedAppointments);
      localStorage.setItem(`appointments_${slug}`, JSON.stringify(updatedAppointments));
      
      setStep(4);
    }
  };

  // Formatar data
  const formatDate = (date) => {
    return date.toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!professional) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <h1 className="text-2xl font-bold text-slate-900 mb-4">Agenda não encontrada</h1>
        <p className="text-slate-600 mb-8">Este link não existe ou foi desativado.</p>
        <Button onClick={() => navigate('/agenda')} variant="outline">
          <ArrowLeft size={18} />
          Voltar
        </Button>
      </div>
    );
  }

  const calendarDays = generateCalendarDays();
  const timeSlots = getAvailableSlots();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-slate-900">{professional.business}</h1>
              <p className="text-sm text-slate-500">Agendamento online</p>
            </div>
            {professional.phone && (
              <a
                href={`https://wa.me/55${professional.phone.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-colors"
              >
                <Phone size={20} />
              </a>
            )}
          </div>
          {professional.address && (
            <div className="flex items-center gap-1 mt-2 text-sm text-slate-500">
              <MapPin size={14} />
              {professional.address}
            </div>
          )}
        </div>
      </header>

      {/* Progress */}
      {step < 4 && (
        <div className="bg-white border-b border-slate-100">
          <div className="max-w-3xl mx-auto px-4 py-3">
            <div className="flex items-center gap-2">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    s === step ? 'bg-blue-600 text-white' :
                    s < step ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'
                  }`}>
                    {s < step ? <Check size={16} /> : s}
                  </div>
                  {s < 3 && <div className={`w-8 h-0.5 ${s < step ? 'bg-blue-600' : 'bg-slate-200'}`} />}
                </div>
              ))}
              <span className="text-sm text-slate-500 ml-2">
                {step === 1 ? 'Serviço' : step === 2 ? 'Data e horário' : 'Seus dados'}
              </span>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* Step 1: Escolher serviço */}
        {step === 1 && (
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Escolha o serviço</h2>
            <div className="space-y-3">
              {professional.services.map((service) => (
                <button
                  key={service.id}
                  onClick={() => {
                    setSelectedService(service);
                    setStep(2);
                  }}
                  data-testid={`service-${service.id}`}
                  className={`w-full p-4 rounded-xl border text-left transition-all ${
                    selectedService?.id === service.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 bg-white hover:border-blue-300'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-slate-900">{service.name}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-sm text-slate-500 flex items-center gap-1">
                          <Clock size={14} />
                          {formatDuration(service.duration)}
                        </span>
                      </div>
                    </div>
                    <span className="font-semibold text-blue-600">{formatPrice(service.price)}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Escolher data e horário */}
        {step === 2 && (
          <div>
            <button
              onClick={() => setStep(1)}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
            >
              <ArrowLeft size={18} />
              Voltar
            </button>

            {/* Serviço selecionado */}
            <div className="bg-blue-50 rounded-xl p-4 mb-6 border border-blue-100">
              <p className="font-medium text-blue-900">{selectedService?.name}</p>
              <p className="text-sm text-blue-700">
                {formatDuration(selectedService?.duration)} • {formatPrice(selectedService?.price)}
              </p>
            </div>

            {/* Calendário */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
              {/* Header do calendário */}
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <ChevronLeft size={20} />
                </button>
                <h3 className="font-semibold text-slate-900 capitalize">
                  {currentMonth.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                </h3>
                <button
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <ChevronRight size={20} />
                </button>
              </div>

              {/* Dias da semana */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((day) => (
                  <div key={day} className="text-center text-xs font-medium text-slate-500 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Dias */}
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, index) => (
                  <button
                    key={index}
                    onClick={() => day.isAvailable && setSelectedDate(day.date)}
                    disabled={!day.isAvailable || !day.day}
                    className={`aspect-square flex items-center justify-center rounded-lg text-sm transition-all ${
                      !day.day ? '' :
                      selectedDate?.toDateString() === day.date?.toDateString()
                        ? 'bg-blue-600 text-white'
                        : day.isToday
                        ? 'bg-blue-100 text-blue-600 font-semibold'
                        : day.isAvailable
                        ? 'hover:bg-slate-100 text-slate-900'
                        : 'text-slate-300 cursor-not-allowed'
                    }`}
                  >
                    {day.day}
                  </button>
                ))}
              </div>
            </div>

            {/* Horários */}
            {selectedDate && (
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <h3 className="font-semibold text-slate-900 mb-4">
                  Horários para {formatDate(selectedDate)}
                </h3>
                {timeSlots.length > 0 ? (
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                    {timeSlots.map((slot) => (
                      <button
                        key={slot.time}
                        onClick={() => {
                          if (slot.available) {
                            setSelectedTime(slot.time);
                            setStep(3);
                          }
                        }}
                        disabled={!slot.available}
                        className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                          selectedTime === slot.time
                            ? 'bg-blue-600 text-white'
                            : slot.available
                            ? 'bg-slate-100 text-slate-700 hover:bg-blue-100'
                            : 'bg-slate-50 text-slate-300 cursor-not-allowed line-through'
                        }`}
                      >
                        {slot.time}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">
                    Nenhum horário disponível para esta data
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Dados do cliente */}
        {step === 3 && (
          <div>
            <button
              onClick={() => setStep(2)}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
            >
              <ArrowLeft size={18} />
              Voltar
            </button>

            {/* Resumo */}
            <div className="bg-blue-50 rounded-xl p-4 mb-6 border border-blue-100">
              <p className="font-medium text-blue-900">{selectedService?.name}</p>
              <p className="text-sm text-blue-700">
                {formatDate(selectedDate)} às {selectedTime}
              </p>
              <p className="text-sm text-blue-700">
                {formatDuration(selectedService?.duration)} • {formatPrice(selectedService?.price)}
              </p>
            </div>

            {/* Formulário */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Seus dados</h3>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Nome completo</Label>
                  <Input
                    id="name"
                    value={clientData.name}
                    onChange={(e) => setClientData({ ...clientData, name: e.target.value })}
                    placeholder="Seu nome"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">WhatsApp</Label>
                  <Input
                    id="phone"
                    value={clientData.phone}
                    onChange={(e) => setClientData({ ...clientData, phone: e.target.value })}
                    placeholder="(00) 00000-0000"
                    className="mt-2"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Você receberá lembretes neste número
                  </p>
                </div>
              </div>

              <Button
                onClick={handleConfirmAppointment}
                disabled={!clientData.name || !clientData.phone}
                className="btn-primary w-full mt-6"
                data-testid="confirm-appointment-btn"
              >
                Confirmar agendamento
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Confirmado */}
        {step === 4 && (
          <div className="text-center py-12">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <Check size={40} className="text-green-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Agendamento confirmado!</h2>
            <p className="text-slate-600 mb-8">
              Você receberá um lembrete no WhatsApp.
            </p>
            
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 max-w-md mx-auto mb-6">
              <div className="text-left">
                <p className="font-semibold text-slate-900 mb-1">{selectedService?.name}</p>
                <p className="text-slate-600 capitalize">{formatDate(selectedDate)}</p>
                <p className="text-slate-600">às {selectedTime}</p>
                <p className="text-blue-600 font-semibold mt-2">{formatPrice(selectedService?.price)}</p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 max-w-md mx-auto">
              <div className="flex items-center gap-3 mb-3">
                <MapPin size={18} className="text-slate-400" />
                <span className="text-slate-600">{professional.address || professional.business}</span>
              </div>
              <Button
                onClick={() => {
                  const message = `Olá! Acabei de agendar:\n\n📍 ${selectedService?.name}\n📅 ${formatDate(selectedDate)}\n⏰ ${selectedTime}\n\nNome: ${clientData.name}`;
                  window.open(`https://wa.me/55${professional.phone.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`, '_blank');
                }}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                <Phone size={18} />
                Salvar contato
              </Button>
            </div>

            <button
              onClick={() => {
                setStep(1);
                setSelectedService(null);
                setSelectedDate(null);
                setSelectedTime(null);
                setClientData({ name: '', phone: '' });
              }}
              className="mt-8 text-blue-600 hover:text-blue-800 text-sm"
            >
              Fazer novo agendamento
            </button>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-auto py-6 text-center">
        <p className="text-xs text-slate-400">
          Powered by <a href="/" className="text-blue-600 hover:underline">AtivaJá</a>
        </p>
      </footer>
    </div>
  );
};

export default AgendaPublica;
